import { Autocomplete, Box, ListItem, ListItemText, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { WidgetProps } from '@rjsf/utils';
import { useQuery } from '@tanstack/react-query';
import { QueryKey, WorkflowSummary } from 'api/model';
import { getWorkflowsSummary } from 'api/workflows/requests';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { toLocaleDateStr } from 'utils/helpers';
import { RevisionChip } from 'components/table/RevisionChip';

export default function WorkflowSelectWidget(props: WidgetProps<string>) {
  const { value, onChange, required, disabled, formContext } = props;
  const { projectId } = useParams();

  const currentWorkflowId: string | undefined = (formContext as any)?.formData?.workflow_id;
  const enabled = Boolean(projectId);
  const workflowsQueryKey = [QueryKey.Workflows, projectId];

  const { data: workflows = [] } = useQuery({
    queryKey: workflowsQueryKey,
    queryFn: () => getWorkflowsSummary(projectId as string),
    enabled,
    staleTime: 30_000,
  });

  const rows = useMemo(
    () => workflows.filter((workflow) => !currentWorkflowId || workflow.id !== currentWorkflowId),
    [workflows, currentWorkflowId],
  );

  const selected = rows.find((r) => r.id === value) ?? null;

  const filterOptions = useMemo(
    () =>
      createFilterOptions<WorkflowSummary>({
        matchFrom: 'any',
        trim: true,
        stringify: (opt) => {
          const p: string[] = [opt.id, opt.name, `rev:${opt.revision}`, String(opt.revision)];
          if (opt.created_at) {
            p.push(toLocaleDateStr(opt.created_at) ?? '-');
            p.push(new Date(opt.created_at).toISOString());
          }
          return p.join(' ');
        },
      }),
    [],
  );

  return (
    <Box>
      <Autocomplete
        disablePortal
        options={rows}
        filterOptions={filterOptions}
        getOptionLabel={(opt) => opt?.name || opt?.id || ''}
        value={selected}
        onChange={(_, opt) => onChange(opt?.id ?? '')}
        disabled={disabled || rows.length === 0}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Workflow"
            required={required}
            placeholder={rows.length ? 'Search by name, ID, rev, date…' : 'No workflows'}
            InputProps={{
              ...params.InputProps,
              startAdornment: selected ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, pr: 0, overflow: 'hidden' }}>
                  <RevisionChip revision={selected.revision} size="small" />
                </Box>
              ) : undefined,
            }}
          />
        )}
        renderOption={(propsMUI, opt) => (
          <ListItem {...propsMUI} key={opt.id} dense sx={{ gap: 1 }}>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <span
                      style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {opt.name || opt.id}
                    </span>
                  </Box>
                  <RevisionChip revision={opt.revision} size="small" />
                </Box>
              }
              secondary={
                <span style={{ opacity: 0.8 }}>
                  {opt.created_at ? `Created: ${toLocaleDateStr(opt.created_at) ?? '-'}` : ''}
                </span>
              }
            />
          </ListItem>
        )}
        renderTags={() => null}
        isOptionEqualToValue={(a, b) => a.id === b.id}
      />
    </Box>
  );
}
